import React, { useContext } from 'react'
import { UserContext } from '../../../App'

import BusinessIcon from '@mui/icons-material/Business'
import HandshakeIcon from '@mui/icons-material/Handshake'
import BadgeIcon from '@mui/icons-material/Badge'
import GavelIcon from '@mui/icons-material/Gavel'
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled'
import EventIcon from '@mui/icons-material/Event'
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import RadarIcon from '@mui/icons-material/Radar'

import CompanySettings from './CompanySettings/CompanySettings.jsx'
import SiteAdmins from './SiteAdmins/SiteAdmins.jsx'
import QuickLinks from './QuickLinks.jsx'
import BillingPage from './Billing/BillingPage.jsx'
import EngagementLetter from './EngagementLetter/EngagementLetter.jsx'
import Quotes from './CompanySettings/Quotes.jsx'
import ManageAccess from './Employees/ManageAccess.jsx'
import StaffSettings from './StaffSettings.jsx'
import SubcontractorSettings from './Employees/SubcontractorSettings.jsx'
import MandatoryTraining from './MandatoryTraining'
import DBSChecks from './Employees/DBSChecks.jsx'
import AppraisalSettings from './Employees/AppraisalSettings.jsx'
import OccupationalHealthSettings from './Employees/OccupationalHealthSettings.jsx'
import ShiftConfig from './ShiftConfig.jsx'
import Applications from './Applications.jsx'
import PatientRecords from './PatientRecords.jsx'
import Medicines from './Governance/MedicinesFormulary/Medicines.jsx'
import Policies from './Policies.jsx'
import CPRProtocol from './CPRProtocol.jsx'
import SpeakUpSettings from './Governance/SpeakUpSettings.jsx'
import Vehicles from './Vehicles/Vehicles.jsx'
import VehicleCleaning from './VehicleCleaning/VehicleCleaning.jsx'
import VDI from './VDI/VDI.jsx'
import StockCatalog from './MakeReady/StockCatalog.jsx'
import VehicleSafetyCheckSettings from './Vehicles/VehicleSafetyCheckSettings.jsx'
import DefectAlertSettings from './Vehicles/DefectAlertSettings.jsx'
import EventPlanning from './EventPlanning/EventPlanning.jsx'
import MedicalPlans from './MedicalPlans/MedicalPlans.jsx'
import PTSAccess from './PatientTransport/PTSAccess.jsx'
import PTSBookings from './PatientTransport/Bookings.jsx'
import PTSPricingTariffs from './PatientTransport/PricingTariffs.jsx'
import CADAccess from './CommandControl/CADAccess.jsx'
import CallTriage from './CommandControl/CallTriage.jsx'

// Everything the Site Configuration area offers, grouped and role-gated.
// The sidebar, the landing overview, the search and the routes all read from
// this one list, so a new settings page only needs an entry here.
export const useConfigGroups = () => {
  const { user } = useContext(UserContext)
  const teams = user?.employee?.teams || []

  const isSiteAdmin = user?.security?.isSiteAdmin
  const isGovernance = teams.includes('Governance')
  const isHR = teams.includes('Personnel')
  const isFleet = teams.includes('Fleet')
  const managesClients =
    teams.includes('Commercial Training') || teams.includes('Event Planning')
  const isEventPlanner = teams.includes('Event Planning')
  const isPTSCoordinator = user?.employee?.ptsAccess?.includes('CoOrdinator')

  const groups = [
    isSiteAdmin && {
      name: 'Organisation',
      icon: BusinessIcon,
      description: 'Identity, admins and billing for your organisation.',
      items: [
        {
          label: 'Company Settings',
          slug: 'company-settings',
          description: 'Name, address, logo and brand colour.',
          keywords: 'organisation name address logo colour branding email',
          component: <CompanySettings />,
        },
        {
          label: 'Site Admins',
          slug: 'site-admins',
          description: 'Grant or remove site administrator access.',
          keywords: 'administrators permissions superuser',
          component: <SiteAdmins />,
        },
        {
          label: 'Quick Links',
          slug: 'quick-links',
          description: 'Curated links shown to your team.',
          keywords: 'links shortcuts resources',
          component: <QuickLinks />,
        },
        {
          label: 'Billing',
          slug: 'billing',
          description: 'Subscription, payment details and referrals.',
          keywords: 'subscription payment stripe invoice referral credit',
          component: <BillingPage />,
        },
      ],
    },
    (isSiteAdmin || managesClients) && {
      name: 'Clients',
      icon: HandshakeIcon,
      description: 'Templates used when working with clients.',
      items: [
        {
          label: 'Engagement Letter',
          slug: 'engagement-letter',
          description: 'The letter sent to clients when work is agreed.',
          keywords: 'engagement letter template client terms',
          component: <EngagementLetter />,
        },
        {
          label: 'Quotes',
          slug: 'quotes',
          description: 'Defaults used when building client quotes.',
          keywords: 'quotes pricing estimates terms',
          component: <Quotes />,
        },
      ],
    },
    (isSiteAdmin || isHR) && {
      name: 'Employees',
      icon: BadgeIcon,
      description: 'Access, compliance and shift rules for your people.',
      items: [
        {
          label: 'Manage Access',
          slug: 'manage-access',
          description: 'Management teams, CAD and PTS access in one grid.',
          keywords: 'access permissions teams roles matrix cad pts',
          component: <ManageAccess />,
        },
        {
          label: 'Staff',
          slug: 'staff',
          description: 'Staff roles and employment settings.',
          keywords: 'staff roles employment profile',
          component: <StaffSettings />,
        },
        {
          label: 'Subcontractors',
          slug: 'subcontractors',
          description: 'Subcontractor access and defaults.',
          keywords: 'subcontractors external access windows',
          component: <SubcontractorSettings />,
        },
        {
          label: 'Mandatory Training',
          slug: 'mandatory-training',
          description: 'Courses every employee must keep in date.',
          keywords: 'training courses compliance expiry',
          component: <MandatoryTraining />,
        },
        {
          label: 'DBS Checks',
          slug: 'dbs-checks',
          description: 'DBS requirements and update-service monitoring.',
          keywords: 'dbs disclosure barring checks criminal',
          component: <DBSChecks />,
        },
        {
          label: 'Appraisals',
          slug: 'appraisals',
          description: 'Appraisal scheduling and frequency.',
          keywords: 'appraisals reviews performance scheduling',
          component: <AppraisalSettings />,
        },
        {
          label: 'Occupational Health',
          slug: 'occupational-health',
          description: 'Screening questions and booking rules.',
          keywords: 'occupational health screening bookings',
          component: <OccupationalHealthSettings />,
        },
        {
          label: 'Shifts',
          slug: 'shifts',
          description: 'Shift types, pay and booking rules.',
          keywords: 'shifts pay rates booking overtime',
          component: <ShiftConfig />,
        },
        {
          label: 'Applications',
          slug: 'applications',
          description: 'Recruitment application settings.',
          keywords: 'recruitment applications onboarding referees',
          component: <Applications />,
        },
      ],
    },
    (isSiteAdmin || isGovernance) && {
      name: 'Governance',
      icon: GavelIcon,
      description: 'Clinical records, medicines and policy settings.',
      items: [
        {
          label: 'Patient Records',
          slug: 'patient-records',
          description: 'ePCR behaviour and clinical record settings.',
          keywords: 'epcr pcr patient records clinical audits',
          component: <PatientRecords />,
        },
        {
          label: 'Medicines',
          slug: 'medicines',
          description: 'Formulary, stores, bags and bag levels.',
          keywords: 'medicines formulary drugs stores bags controlled',
          component: <Medicines />,
        },
        {
          label: 'Policies',
          slug: 'policies',
          description: 'Policy categories and descriptions.',
          keywords: 'policies documents pgd procedures',
          component: <Policies />,
        },
        {
          label: 'Cardiac Arrest Protocol',
          slug: 'cpr-protocol',
          description: 'The protocol shown during a cardiac arrest.',
          keywords: 'cpr cardiac arrest resus protocol',
          component: <CPRProtocol />,
        },
        {
          label: 'Speak Up',
          slug: 'speak-up',
          description: 'Freedom to Speak Up contacts and routing.',
          keywords: 'speak up whistleblowing concerns freedom',
          component: <SpeakUpSettings />,
        },
      ],
    },
    (isSiteAdmin || isFleet) && {
      name: 'Fleet',
      icon: DirectionsCarFilledIcon,
      description: 'Vehicles, inspections and make-ready stock.',
      items: [
        {
          label: 'Vehicles',
          slug: 'vehicles',
          description: 'Your vehicles, types and fleet defaults.',
          keywords: 'vehicles ambulances fleet types archived',
          component: <Vehicles />,
        },
        {
          label: 'Vehicle Cleaning',
          slug: 'vehicle-cleaning',
          description: 'Cleaning checklists and deep-clean frequency.',
          keywords: 'cleaning deep clean schedules checklist',
          component: <VehicleCleaning />,
        },
        {
          label: 'VDI',
          slug: 'vdi',
          description: 'Daily vehicle inspection checklists.',
          keywords: 'vdi inspection daily checks checklist',
          component: <VDI />,
        },
        {
          label: 'Stock Catalog',
          slug: 'stock-catalog',
          description: 'Items available to Make Ready load lists.',
          keywords: 'stock catalogue consumables make ready inventory',
          component: <StockCatalog />,
        },
        {
          label: 'Safety Checks',
          slug: 'vehicle-safety-checks',
          description: 'Recurring vehicle safety check templates.',
          keywords: 'safety checks loler servicing recurring',
          component: <VehicleSafetyCheckSettings />,
        },
        {
          label: 'Defect Alerts',
          slug: 'defect-alerts',
          description: 'Who is alerted when a defect is raised.',
          keywords: 'defects alerts contacts out of service',
          component: <DefectAlertSettings />,
        },
      ],
    },
    (isSiteAdmin || isEventPlanner) && {
      name: 'Events',
      icon: EventIcon,
      description: 'Event planning defaults and medical plans.',
      items: [
        {
          label: 'Event Planning',
          slug: 'event-planning',
          description: 'Event defaults, documents and workflows.',
          keywords: 'events planning defaults shifts',
          component: <EventPlanning />,
        },
        {
          label: 'Medical Plans',
          slug: 'medical-plans',
          description: 'EMP templates and hospital destinations.',
          keywords: 'emp medical plans templates hospitals destinations',
          component: <MedicalPlans />,
        },
      ],
    },
    (isSiteAdmin || isHR || isPTSCoordinator) && {
      name: 'Patient Transport',
      icon: AirportShuttleIcon,
      description: 'PTS access, bookings and pricing.',
      items: [
        {
          label: 'PTS Access',
          slug: 'pts-access',
          description: 'Who can co-ordinate and crew PTS.',
          keywords: 'pts access coordinator crew permissions',
          component: <PTSAccess />,
        },
        {
          label: 'Bookings',
          slug: 'pts-bookings',
          description: 'Booking form presets and dataset rules.',
          keywords: 'pts bookings presets minimum dataset journeys',
          component: <PTSBookings />,
        },
        {
          label: 'Pricing & Tariffs',
          slug: 'pts-pricing',
          description: 'Journey pricing and tariff bands.',
          keywords: 'pts pricing tariffs mileage invoicing',
          component: <PTSPricingTariffs />,
        },
      ],
    },
    (isSiteAdmin || isGovernance) && {
      name: 'Command and Control',
      icon: RadarIcon,
      description: 'Control room access and call triage.',
      items: [
        ...(isSiteAdmin
          ? [
              {
                label: 'CAD Access',
                slug: 'cad-access',
                description: 'Who can use the control room.',
                keywords: 'cad control room dispatch access',
                component: <CADAccess />,
              },
            ]
          : []),
        {
          label: 'Call Triage',
          slug: 'command-control-call-triage',
          description: 'Triage questions for incoming calls.',
          keywords: 'call triage questions 999 dispatch',
          component: <CallTriage />,
        },
      ],
    },
  ].filter(Boolean)

  return { groups, isSiteAdmin }
}

// The item whose slug matches the current /site-configuration/* path, used
// for the contextual header above each subpage.
export const findConfigItem = (groups, pathname) => {
  const current = pathname.replace(/^\/site-configuration\/?/, '').split('/')[0]
  for (const group of groups) {
    const item = group.items.find((i) => i.slug === current)
    if (item) return { group, item }
  }
  return null
}

export default useConfigGroups
